import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart, Cpu, Sparkles, Server } from "lucide-react";
import { NovaMark } from "./brand";

// Portfolio credit links - this footer is the ONE place AWS service names + the
// author are intentionally surfaced (the published Builder Center piece).
const LINKEDIN_URL = "https://www.linkedin.com/in/yogesh-selvarajan/";
const BUILDER_CENTER_URL = "https://builder.aws.com/community/@yogeshs";

// The "Built on AWS" stack, branded by name here only (everywhere else stays
// product-facing, no infra leak). lucide icons, no emojis.
const AWS_STACK = [
  { label: "Amazon Bedrock", Icon: Cpu },
  { label: "Amazon Nova", Icon: Sparkles },
  { label: "Bedrock AgentCore Runtime", Icon: Server },
] as const;

/**
 * SiteFooter - the single, fully-fledged footer rendered at the bottom of EVERY
 * page (public landing AND the authed app shell). Two link columns (Product +
 * Resources - Legal/Company intentionally removed), a brand blurb, a "Built on
 * AWS" tech-stack strip, and the author credit. This footer is the ONE place
 * AWS service names + the author are intentionally surfaced (it is a published
 * AWS Builder Center portfolio piece); operational internals stay hidden
 * everywhere else.
 *
 * Every link resolves to a real destination - no dead labels, no href="#":
 *   - variant="landing": Product links are in-page section anchors on the
 *     marketing page (#how-it-works, #specialists, ...). They smooth-scroll when
 *     the user is already on the landing route, and navigate cross-route to
 *     "/#section" (so the landing page opens and scrolls to it) otherwise.
 *   - variant="app": Product links map to the real authed routes (Watchlist,
 *     Analyze via callbacks); the feature extras (Threat scoring, Battlecards,
 *     Export briefs) deep-link to the matching explainer section in /guides so
 *     they teach the feature rather than dead-end.
 *   - Resources on BOTH variants route to the real public resource pages
 *     (/guides, /playbooks, /status) via the router.
 */

type LinkKind =
  /** Smooth-scroll to an in-page section on the landing route (cross-route safe). */
  | { kind: "anchor"; section: string }
  /** Client-side route navigation (optionally to a "#hash" on that route). */
  | { kind: "route"; to: string }
  /** Imperative callback (authed in-app nav). */
  | { kind: "action"; onClick?: () => void };

interface FooterLink {
  label: string;
  target: LinkKind;
}

interface FooterColumn {
  heading: string;
  links: FooterLink[];
}

export interface SiteFooterProps {
  variant: "landing" | "app";
  /** Authed nav helpers (only used by variant="app"). */
  onWatchlist?: () => void;
  onAnalyze?: () => void;
}

function buildColumns({ variant, onWatchlist, onAnalyze }: SiteFooterProps): FooterColumn[] {
  const product: FooterLink[] =
    variant === "landing"
      ? [
          { label: "How it works", target: { kind: "anchor", section: "how-it-works" } },
          { label: "Specialists", target: { kind: "anchor", section: "specialists" } },
          { label: "Why NovaSynth", target: { kind: "anchor", section: "why" } },
          { label: "What you get", target: { kind: "anchor", section: "showcase" } },
          { label: "FAQ", target: { kind: "anchor", section: "faq" } },
        ]
      : [
          { label: "Watchlist", target: { kind: "action", onClick: onWatchlist } },
          { label: "Run an analysis", target: { kind: "action", onClick: onAnalyze } },
          // Feature extras teach the feature via the matching guide section.
          { label: "Threat scoring", target: { kind: "route", to: "/guides#threat-scoring" } },
          { label: "Battlecards", target: { kind: "route", to: "/guides#battlecards" } },
          { label: "Export briefs", target: { kind: "route", to: "/guides#export" } },
        ];

  // Resources route to the real public resource pages on both variants.
  const resources: FooterLink[] = [
    { label: "Product guide", target: { kind: "route", to: "/guides" } },
    { label: "Playbooks", target: { kind: "route", to: "/playbooks" } },
    { label: "Status", target: { kind: "route", to: "/status" } },
  ];

  return [
    { heading: "Product", links: product },
    { heading: "Resources", links: resources },
  ];
}

export function SiteFooter(props: SiteFooterProps) {
  const columns = buildColumns(props);

  return (
    <footer
      data-print="hide"
      className="relative z-10 mt-20 border-t border-[#1a2640]/60 bg-[#020617]/60"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Brand column */}
          <div className="md:col-span-6 lg:col-span-7 max-w-md">
            <div className="flex items-center gap-2.5">
              <span className="relative w-9 h-9 rounded-xl flex items-center justify-center bg-brand-700/25 border border-brand-600/30 text-[#22d3ee]">
                <NovaMark className="w-5 h-5" />
              </span>
              <span className="font-mono font-bold tracking-tight text-white text-base">NovaSynth</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-500">
              Real-time competitive intelligence - five specialists, one threat-scored brief, every
              claim cited.
            </p>
          </div>

          {/* Link columns */}
          <div className="md:col-span-6 lg:col-span-5 grid grid-cols-2 gap-8">
            {columns.map((col) => (
              <div key={col.heading}>
                <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {col.heading}
                </h4>
                <ul className="mt-3 flex flex-col gap-2">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <FooterLinkItem link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ── Built on AWS - the portfolio "tech stack" strip ── */}
        <div className="mt-10 border-t border-[#1a2640]/60 pt-6">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Built on AWS
            </span>
            <span className="hidden sm:inline text-slate-800">&middot;</span>
            <ul className="flex flex-wrap items-center gap-1.5">
              {AWS_STACK.map(({ label, Icon }) => (
                <li key={label}>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#1a2640] bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-400">
                    <Icon className="w-3 h-3 text-[#22d3ee]" aria-hidden="true" />
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Copyright + author credit ── */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-[#1a2640]/60 pt-6">
          <p className="text-[11px] text-slate-600">
            NovaSynth &copy; {new Date().getFullYear()}
            <span className="mx-2 text-slate-800">&middot;</span>
            The competitive intelligence platform
          </p>
          <p className="inline-flex flex-wrap items-center justify-center gap-1.5 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              Developed with
              <Heart className="w-3.5 h-3.5 fill-rose-500 text-rose-500" aria-label="love" />
              by
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-slate-300 underline decoration-slate-700 underline-offset-2 transition-colors hover:text-[#22d3ee] hover:decoration-[#22d3ee]"
              >
                Yogesh Selvarajan
              </a>
            </span>
            <span className="text-slate-800">&middot;</span>
            <a
              href={BUILDER_CENTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-slate-400 underline decoration-slate-700 underline-offset-2 transition-colors hover:text-[#22d3ee] hover:decoration-[#22d3ee]"
            >
              AWS Builder Center
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

/** Renders the right element for each link kind, all using real navigation. */
function FooterLinkItem({ link }: { link: FooterLink }) {
  const navigate = useNavigate();
  const location = useLocation();
  const cls = "text-sm text-slate-400 transition-colors hover:text-[#22d3ee] cursor-pointer text-left";

  const { target, label } = link;

  if (target.kind === "action") {
    return (
      <button type="button" onClick={target.onClick} className={cls}>
        {label}
      </button>
    );
  }

  if (target.kind === "route") {
    return (
      <button type="button" onClick={() => navigateToRoute(navigate, target.to)} className={cls}>
        {label}
      </button>
    );
  }

  // Anchor: smooth-scroll on the landing route, cross-route navigate otherwise.
  return <AnchorLink section={target.section} label={label} cls={cls} navigate={navigate} pathname={location.pathname} />;
}

/** Navigate to a route that may carry a "#hash"; scrolls to the hash after nav. */
function navigateToRoute(navigate: ReturnType<typeof useNavigate>, to: string) {
  navigate(to);
  const hashIndex = to.indexOf("#");
  if (hashIndex >= 0) {
    const id = to.slice(hashIndex + 1);
    // Defer until the destination route has mounted, then scroll to the anchor.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  } else {
    // Plain route change: start at the top.
    requestAnimationFrame(() => window.scrollTo({ top: 0 }));
  }
}

function AnchorLink({
  section,
  label,
  cls,
  navigate,
  pathname,
}: {
  section: string;
  label: string;
  cls: string;
  navigate: ReturnType<typeof useNavigate>;
  pathname: string;
}) {
  const onLanding = pathname === "/";

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (onLanding) {
        // Already on the landing page: smooth-scroll to the section in place.
        e.preventDefault();
        document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // On another route: go to the landing page and scroll to the section.
        e.preventDefault();
        navigate(`/#${section}`);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        });
      }
    },
    [onLanding, section, navigate],
  );

  // Real href keeps it a valid, right-clickable link; onClick handles SPA nav.
  return (
    <a href={`/#${section}`} onClick={onClick} className={cls}>
      {label}
    </a>
  );
}
